/**
 * A very small observable store.
 *
 * Deliberately not a framework: state is a plain object, views subscribe
 * to the slices they care about and re-render. On a mid-range Android
 * this is faster and far smaller than a virtual DOM.
 */

export function createStore(initialState) {
  let state = { ...initialState };
  const listeners = new Set();

  function get() {
    return state;
  }

  function set(patch) {
    const next = typeof patch === 'function' ? patch(state) : patch;
    if (!next) return state;
    const changedKeys = Object.keys(next).filter((key) => !Object.is(state[key], next[key]));
    if (!changedKeys.length) return state;
    const previous = state;
    state = { ...state, ...next };
    for (const listener of listeners) {
      try {
        listener(state, previous, changedKeys);
      } catch (error) {
        console.error('[store] listener failed', error);
      }
    }
    return state;
  }

  function subscribe(listener, { immediate = false } = {}) {
    listeners.add(listener);
    if (immediate) listener(state, state, Object.keys(state));
    return () => listeners.delete(listener);
  }

  return { get, set, subscribe };
}

export const VIEWS = ['studio', 'scenes', 'library', 'generate', 'history', 'settings'];

export const appStore = createStore({
  ready: false,
  view: 'studio',
  /** Uploaded source audio */
  audio: null, // { name, size, type, duration, blob }
  /** Scene currently attached to the studio */
  selectedScene: null,
  sceneMode: 'random',
  /** Procedural fallback renderer id when no asset exists */
  proceduralSceneId: null,
  settings: null,
  library: [],
  prompts: [],
  history: [],
  providerStatus: {},
  render: {
    status: 'idle', // idle | preparing | rendering | done | error | cancelled
    progress: 0,
    phase: '',
    elapsedSeconds: 0,
    totalSeconds: 0,
    result: null, // { blob, url, mimeType, container, extension, width, height, durationSeconds, bytes }
    error: null,
  },
  toast: null,
  busy: false,
});
